import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError, API_BASE, apiFetch } from "@/lib/api";
import {
  Plus, Trash, DownloadSimple, Buildings, Package, ArrowRight,
  CaretDown, CaretUp, Receipt, FileText, PencilSimple, PaperPlaneTilt, CurrencyDollar, X, Eye, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { CatalogCard, SalesStatsCard } from "./CatalogCard";
import { CustomersCard, dueLabel, validLabel } from "./CustomersCard";
import { PeriodReportCard, TaxMatrixCard } from "./ReportsCard";
import { PromoCodesCard } from "./PromoCodesCard";
import { PdfCanvasViewer } from "../PdfCanvasViewer";

const fmt = (c) => `$${((c || 0) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


export default function InvoicesPanel({ onRefresh }) {
  const [list, setList] = useState([]);
  const [identity, setIdentity] = useState(null);
  const [products, setProducts] = useState([]); // master catalogue
  const [builder, setBuilder] = useState(null); // { kind, initial } | null
  const [pending, setPending] = useState(null);
  const [payInv, setPayInv] = useState(null);
  const [sendDlg, setSendDlg] = useState(null);
  const [snippets, setSnippets] = useState([]);
  const msgRef = useRef(null);
  const [convDlg, setConvDlg] = useState(null); // { inv, idx } — pick which option before converting
  const [chargeDlg, setChargeDlg] = useState(null); // { amount, description, charge? , status }
  const [chargePresets, setChargePresets] = useState(null);
  const [chargeHist, setChargeHist] = useState(null); // null = collapsed; [] = open
  const [histDlg, setHistDlg] = useState(null); // invoice getting a payment recorded
  const [seatPrice, setSeatPrice] = useState("25.00");
  const [err, setErr] = useState("");
  const [moneyFilter, setMoneyFilter] = useState(null);
  const [customerFilter, setCustomerFilter] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [seatBlocks, setSeatBlocks] = useState([]);
  const [blockForm, setBlockForm] = useState({ board_id: "", count: "", note: "", demo: false, expires_on: "" });
  const [viewDoc, setViewDoc] = useState(null); // { url, number, kind }

  const viewPdf = async (inv) => {
    try {
      const res = await apiFetch(`${API_BASE}/admin/invoices/${inv.id}/pdf`);
      if (!res.ok) throw new Error("Could not load the document");
      const blob = await res.blob();
      setViewDoc({ blob, url: URL.createObjectURL(blob), number: inv.invoice_number, kind: inv.kind || "invoice" });
    } catch (e) { toast.error(e.message); }
  };
  const viewQuoteSnapshot = async (inv) => {
    try {
      const res = await apiFetch(`${API_BASE}/admin/invoices/${inv.id}/quote-snapshot/pdf`);
      if (!res.ok) throw new Error("Could not load the saved quote");
      const blob = await res.blob();
      setViewDoc({ blob, url: URL.createObjectURL(blob), number: `${inv.invoice_number}-original`, kind: "quote" });
    } catch (e) { toast.error(e.message); }
  };
  const closeView = () => {
    if (viewDoc?.url) URL.revokeObjectURL(viewDoc.url);
    setViewDoc(null);
  };
  const printView = () => {
    if (!viewDoc?.url) return;
    const f = document.createElement("iframe");
    f.style.display = "none";
    f.src = viewDoc.url;
    f.onload = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(viewDoc.url, "_blank"); } };
    document.body.appendChild(f);
  };

  const addSeatBlock = async () => {
    const n = parseInt(blockForm.count, 10);
    if (!n || n <= 0) { toast.error("Enter how many seats were purchased"); return; }
    try {
      const { data } = await api.post("/admin/billing/seat-blocks", {
        board_id: blockForm.board_id || null, count: n, note: blockForm.note.trim() || null,
        demo: blockForm.demo, expires_on: blockForm.expires_on || null,
      });
      toast.success(`Seat block added${data.absorbed_pending_seats ? ` — absorbed ${data.absorbed_pending_seats} pending seat(s)` : ""}`);
      setBlockForm({ board_id: "", count: "", note: "", demo: false, expires_on: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeSeatBlock = async (blk) => {
    if (!window.confirm(`Remove this block of ${blk.count} seats? Uninvoiced seats it covered become billable again.`)) return;
    try { await api.delete(`/admin/billing/seat-blocks/${blk.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    try {
      const [l, i, p, cu] = await Promise.all([
        api.get("/admin/invoices"),
        api.get("/admin/business-identity"),
        api.get("/admin/catalog"),
        api.get("/admin/customers"),
      ]);
      setList(l.data); setIdentity(i.data); setProducts(p.data); setCustomers(cu.data);
    } catch (e) { setErr(formatApiError(e)); }
    try { const { data } = await api.get("/admin/billing/pending"); setPending(data); } catch { /* non-admin */ }
    try { const { data } = await api.get("/admin/billing/seat-blocks"); setSeatBlocks(data.boards || []); } catch { /* non-admin */ }
  };
  useEffect(() => { load(); }, []);

  const draftSeatInvoice = (g) => {
    setBuilder({
      kind: "invoice",
      initial: {
        customer_name: g.board_name,
        items: [{ name: `Student seats — ${g.board_name}`, description: `${g.count} seat${g.count === 1 ? "" : "s"}`, quantity: g.count, unit_price_cents: 0 }],
        seat_ledger_board_id: g.board_id || "none",
      },
    });
  };

  const deleteDoc = async (inv) => {
    const kindLabel = inv.kind === "quote" ? "Quote" : "Invoice";
    try {
      await api.delete(`/admin/invoices/${inv.id}`);
      await load(); onRefresh && onRefresh();
      toast(`${kindLabel} ${inv.invoice_number} deleted`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/admin/invoices/${inv.id}/restore`);
              await load(); onRefresh && onRefresh();
              toast.success(`${kindLabel} ${inv.invoice_number} restored`);
            } catch (e) { toast.error(formatApiError(e)); }
          },
        },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const convert = async (id, chosenIdx = null) => {
    try {
      const { data } = await api.post(`/admin/invoices/${id}/convert`,
        chosenIdx === null ? {} : { chosen_option_index: chosenIdx });
      toast.success(`Quote ${data.invoice_number} is now invoice ${data.invoice_number} — same number, due ${data.due_date}`);
      setConvDlg(null);
      await load(); onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendEmail = (inv) => {
    const senders = identity?.sender_emails || [];
    const label = inv.kind === "quote" ? "quote" : "invoice";
    const amount = inv.options_mode
      ? `presenting ${(inv.items || []).length} options — each priced individually`
      : `for ${fmt(inv.total_cents)} CAD`;
    const signature = [identity?.business_name || "Elevation650", identity?.email, identity?.phone]
      .filter(Boolean).join("\n");
    const defaultMsg =
      `Hello ${inv.customer_name},\n\n` +
      `Please find attached ${label} ${inv.invoice_number} ${amount}.\n\n` +
      (inv.notes ? `${inv.notes}\n\n` : "") +
      signature;
    setSendDlg({
      inv,
      to: inv.customer_email || "",
      from_email: senders.includes(identity?.last_sender) ? identity.last_sender : "",
      subject: `${inv.kind === "quote" ? "Quote" : "Invoice"} ${inv.invoice_number} from ${identity?.business_name || "Elevation650"}`,
      message: defaultMsg, cc: "", bcc_me: false,
    });
    api.get("/admin/email-snippets").then(({ data }) => setSnippets(data)).catch(() => {});
  };

  const insertSnippet = (text) => {
    const el = msgRef.current;
    setSendDlg((d) => {
      if (!d) return d;
      const msg = d.message || "";
      const pos = el && document.activeElement === el ? el.selectionStart : null;
      if (pos === null || pos === undefined) {
        return { ...d, message: msg ? `${msg}\n\n${text}` : text };
      }
      return { ...d, message: msg.slice(0, pos) + text + msg.slice(el.selectionEnd) };
    });
  };

  const saveSnippet = async () => {
    const el = msgRef.current;
    const selText = el && el.selectionStart !== el.selectionEnd
      ? sendDlg.message.slice(el.selectionStart, el.selectionEnd) : sendDlg.message;
    if (!selText.trim()) { toast.error("Nothing to save — write or highlight some text first"); return; }
    const title = window.prompt("Name this snippet (e.g. 'Warm intro', 'TEDx sign-off'):");
    if (!title || !title.trim()) return;
    try {
      const { data } = await api.post("/admin/email-snippets", { title: title.trim(), text: selText.trim() });
      setSnippets((s) => [...s, data].sort((a, b) => a.title.localeCompare(b.title)));
      toast.success(`Snippet "${data.title}" saved`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSnippet = async (s) => {
    if (!window.confirm(`Delete snippet "${s.title}"?`)) return;
    try {
      await api.delete(`/admin/email-snippets/${s.id}`);
      setSnippets((list) => list.filter((x) => x.id !== s.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doSend = async () => {
    const { inv, to, from_email, subject, message, cc, bcc_me } = sendDlg;
    if (!to || !to.includes("@")) { toast.error("Enter a recipient email"); return; }
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/send-email`,
        { to_email: to, from_email, subject: subject || null, message: message || null, cc: cc || null, bcc_me: !!bcc_me });
      toast.success(`Sent to ${data.sent_to}`);
      setIdentity((id0) => (id0 ? { ...id0, last_sender: from_email } : id0));
      setSendDlg(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createCharge = async () => {
    const cents = Math.round(parseFloat(chargeDlg.amount || "0") * 100);
    try {
      const { data } = await api.post("/admin/charge", { amount_cents: cents, description: chargeDlg.description });
      setChargeDlg((d) => ({ ...d, charge: data, status: "pending" }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    if (!chargeDlg || chargePresets !== null) return;
    api.get("/admin/catalog").then(({ data }) => {
      const out = [];
      (data || []).forEach((p) => {
        if (p.base_price_cents > 0) out.push({ label: p.name, cents: p.base_price_cents });
        (p.variations || []).forEach((v) => {
          if (v.price_cents > 0) out.push({ label: `${p.name} — ${v.label}`, cents: v.price_cents });
        });
      });
      setChargePresets(out.slice(0, 8));
    }).catch(() => setChargePresets([]));
  }, [chargeDlg, chargePresets]);

  useEffect(() => {
    if (!chargeDlg?.charge || chargeDlg.status === "paid") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/charge/${chargeDlg.charge.charge_id}/status`);
        if (data.status === "paid") {
          setChargeDlg((d) => d ? { ...d, status: "paid" } : d);
          toast.success(`Payment received — ${fmt(chargeDlg.charge.amount_cents)}`);
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [chargeDlg?.charge, chargeDlg?.status]);

  const openHistory = async (inv) => {
    try {
      const { data } = await api.get("/admin/comms-log", { params: { ref_id: inv.id } });
      setHistDlg({ inv, rows: data });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateAll = async () => {
    const cents = seatPrice.trim() ? Math.round(parseFloat(seatPrice) * 100) : null;
    if (seatPrice.trim() && (!cents || cents <= 0)) { toast.error("Seat price must be a positive amount (or leave blank for per-board pricing)"); return; }
    if (!window.confirm(`Generate invoices for ALL ${pending.groups.length} board group(s) at ${cents ? `${fmt(cents)}/seat` : "each board's configured seat price"}? Their unbilled seats will be marked as billed.`)) return;
    try {
      const { data } = await api.post("/admin/invoices/generate-all", cents ? { unit_price_cents: cents } : {});
      if (data.skipped?.length) toast.warning(`Skipped ${data.skipped.map((s) => s.board_name).join(", ")} — no school-seat price tagged in the Master catalogue (or type a $/seat).`);
      if (data.count) toast.success(`Created ${data.count} invoice(s) — they're in the list below.`);
      await load(); onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendAllUnsent = async () => {
    if (!window.confirm("Email every never-sent invoice that has a customer email (PDF attached)?")) return;
    try {
      const { data } = await api.post("/admin/invoices/send-all");
      if (data.eligible === 0) toast.info("No unsent invoices with an email address.");
      else if (data.failed) toast.error(`Sent ${data.sent}, failed ${data.failed} (${data.failed_numbers.slice(0, 3).join(", ")}${data.failed_numbers.length > 3 ? "…" : ""}) — check your Resend domain.`);
      else toast.success(`Sent ${data.sent} invoice(s)`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const payChip = (inv) => {
    if (inv.payment_status === "paid") return { label: "paid", bg: "#DCFCE7", color: "#166534", border: "#86EFAC" };
    if (inv.overdue) return { label: `overdue · ${fmt(inv.balance_cents)} due`, bg: "#FEE2E2", color: "#B91C1C", border: "#FCA5A5" };
    if (inv.payment_status === "partial") return { label: `part-paid · ${fmt(inv.balance_cents)} due`, bg: "#FEF3C7", color: "#92400E", border: "#FDE68A" };
    return { label: "unpaid", bg: "#F4F4F5", color: "#000000", border: "#D4D4D8" };
  };

  return (
    <div className="flex flex-col gap-5">
      <BusinessIdentityCard identity={identity} onSaved={setIdentity} />
      <CustomersCard
        customers={customers}
        onChanged={setCustomers}
        onFilter={(name) => setCustomerFilter(name)}
        onView={viewPdf}
        onNewDoc={(k, c) => {
          setBuilder({ kind: k, initial: { customer_id: c.id, customer_name: c.name, customer_address: c.address || "", customer_email: c.email || "", customer_phone: c.phone || "" } });
          window.scrollTo({ top: 0, behavior: "smooth" });
        }} />
      <CatalogCard catalog={products} onChanged={setProducts} />
      <SalesStatsCard />
      <PeriodReportCard />
      <TaxMatrixCard />
      <PromoCodesCard />
      <StripeSalesCard />

      <MoneyStrip list={list} filter={moneyFilter} onFilter={setMoneyFilter} />

      <div className="card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-xl font-bold font-display">Invoices &amp; Quotes ({list.length})</h2>
          <div className="flex gap-2 flex-wrap items-center">
            <label className="flex items-center gap-1.5 text-xs font-semibold text-[#000000] cursor-pointer mr-1"
              title="Kill-switch for the automatic overdue reminder emails only — invoices, orders and seats are never touched.">
              <input type="checkbox" data-testid="overdue-nudges-toggle"
                checked={identity?.overdue_nudges_enabled !== false}
                onChange={async (e) => {
                  try {
                    const { data } = await api.put("/admin/business-identity", { overdue_nudges_enabled: e.target.checked });
                    setIdentity(data);
                    toast.success(e.target.checked ? "Overdue reminders ON — friendly nudges at 7-day intervals (max 3)" : "Overdue reminders OFF — no automatic emails will go out");
                  } catch (err) { toast.error(formatApiError(err)); }
                }} />
              Overdue reminders
            </label>
            <button data-testid="qr-charge-button" className="btn-secondary text-sm flex items-center gap-1"
              onClick={() => setChargeDlg({ amount: "", description: "" })}>
              Charge (QR)
            </button>
            <button data-testid="qr-history-button" className="btn-ghost text-xs underline-offset-2 hover:underline"
              onClick={async () => {
                if (chargeHist) { setChargeHist(null); return; }
                try { const { data } = await api.get("/admin/charges"); setChargeHist(data); }
                catch (e) { toast.error(formatApiError(e)); }
              }}>
              {chargeHist ? "Hide QR charges" : "QR charge history"}
            </button>
            <a data-testid="export-csv-button" className="btn-secondary text-sm flex items-center gap-1"
              href={`${API_BASE}/admin/invoices/export.csv`} target="_blank" rel="noopener noreferrer">
              <DownloadSimple size={15} weight="bold" /> Export CSV
            </a>
            <button data-testid="new-quote-button" className="btn-secondary flex items-center gap-1" onClick={() => setBuilder({ kind: "quote", initial: null })}>
              <FileText size={16} weight="bold" /> New quote
            </button>
            <button data-testid="new-invoice-button" className="btn-primary flex items-center gap-1" onClick={() => setBuilder({ kind: "invoice", initial: null })}>
              <Plus size={16} weight="bold" /> New invoice
            </button>
          </div>
        </div>

        {chargeHist && (
          <div className="mb-5 rounded-2xl border border-[#C4C8CE] bg-white p-4" data-testid="qr-charge-history">
            <div className="font-bold font-display text-sm mb-2">QR charges</div>
            {chargeHist.length === 0 && <div className="text-sm text-[#000000]">No QR charges yet.</div>}
            {chargeHist.map((c) => (
              <div key={c.id} className="flex items-center gap-3 text-sm border-b border-[#F1F1F2] py-1.5 last:border-0" data-testid={`qr-charge-row-${c.id}`}>
                <span className="text-xs text-[#000000] w-36 shrink-0">{new Date(c.created_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                <span className="flex-1 min-w-0 truncate">{c.description || "In-person payment"}</span>
                <span className="font-bold">{fmt(c.amount_cents)}</span>
                <span className="chip text-[10px] font-bold uppercase"
                  style={c.status === "paid" ? { background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" } : { background: "#FEF3C7", color: "#92400E", border: "1px solid #FBBF24" }}>
                  {c.status}
                </span>
              </div>
            ))}
          </div>
        )}

        {pending && (
          <div data-testid="pending-billing-section" className="mb-5 rounded-2xl border border-[#BEF264] bg-[#F7FEE7] p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="font-bold font-display text-sm text-[#3F6212]">Unbilled student seats & renewals</div>
              {(pending.groups || []).length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-1 text-xs font-bold text-[#3F6212]">
                  $/seat
                  <input data-testid="seat-price-input" className="input-base w-24 text-right" style={{ padding: "3px 8px" }}
                    placeholder="auto" title="Leave blank to use each board's configured seat price (Pricing card)"
                    value={seatPrice} onChange={(e) => setSeatPrice(e.target.value)} />
                </label>
                <button data-testid="generate-all-button" className="btn-primary text-xs" style={{ padding: "5px 12px" }} onClick={generateAll}>
                  Generate all invoices
                </button>
                <button data-testid="send-all-button" className="btn-secondary text-xs flex items-center gap-1" style={{ padding: "5px 12px" }} onClick={sendAllUnsent}>
                  <PaperPlaneTilt size={13} weight="bold" /> Send all unsent
                </button>
              </div>
              )}
            </div>
            {(pending.groups || []).length === 0 ? (
              <p data-testid="pending-billing-empty" className="text-sm text-[#3F6212]">
                All caught up — no unbilled seats right now. New students consume prepaid seat blocks first; once a board's block runs out, overflow seats appear here, ready to invoice.
              </p>
            ) : (
            <>
            <div className="space-y-2">
              {pending.groups.map(g => (
                <div key={g.board_id || "independent"} data-testid={`pending-board-${g.board_id || "independent"}`} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#D9F99D] px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{g.board_name} — {g.count} seat{g.count === 1 ? "" : "s"}</div>
                    <div className="text-xs text-[#000000] truncate">{(g.schools || []).map(s => `${s.name} (${s.count})`).join(" · ")}</div>
                  </div>
                  <button data-testid={`draft-seat-invoice-${g.board_id || "independent"}`} className="btn-secondary text-xs leading-none shrink-0" style={{ padding: "5px 12px" }} onClick={() => draftSeatInvoice(g)}>Draft invoice</button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#3F6212]/80 mt-2">Seats added by boards, schools &amp; teachers land here automatically. Generating a drafted invoice marks them as billed.</p>
            </>
            )}
            {seatBlocks.length > 0 && (
              <div data-testid="seat-blocks-section" className="mt-3 pt-3 border-t border-[#BEF264]">
                <div className="font-bold text-xs uppercase tracking-[0.1em] text-[#3F6212] mb-2">Prepaid seat blocks</div>
                {seatBlocks.filter((b) => b.allowance > 0).map((b) => (
                  <div key={b.board_id || "none"} data-testid={`seat-block-board-${b.board_id || "none"}`} className="flex items-center gap-2 flex-wrap text-sm text-[#3F6212] mb-1">
                    <strong>{b.board_name}</strong>
                    <span>— {b.used.toLocaleString()} of {b.allowance.toLocaleString()} seats used ({b.remaining.toLocaleString()} left)</span>
                    <div className="h-1.5 rounded-full bg-white border border-[#BEF264] w-32 overflow-hidden">
                      <div className="h-full bg-[#84CC16]" style={{ width: `${b.allowance ? Math.min(100, (b.used / b.allowance) * 100) : 0}%` }} />
                    </div>
                    {b.blocks.map((blk) => (
                      <span key={blk.id} className={`inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 ${blk.demo ? "bg-[#FEF3C7] border border-[#F59E0B] text-[#92400E]" : "bg-white border border-[#BEF264]"}`} title={blk.note || ""}>
                        {blk.demo && <b>DEMO</b>} +{blk.count.toLocaleString()}{blk.expires_on ? ` · exp ${blk.expires_on}` : ""}{blk.note ? ` · ${blk.note}` : ""}
                        <button data-testid={`remove-seat-block-${blk.id}`} onClick={() => removeSeatBlock(blk)} className="btn-ghost p-0 leading-none text-[#3F6212]" title="Remove this block">×</button>
                      </span>
                    ))}
                  </div>
                ))}
                {seatBlocks.every((b) => b.allowance === 0) && (
                  <p className="text-xs text-[#3F6212]/80 mb-1">No prepaid blocks yet — every new seat is billable. Add a block when a board pre-buys seats.</p>
                )}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <select data-testid="seat-block-board" className="input-base text-xs" style={{ width: "14rem", flex: "0 0 auto" }}
                    value={blockForm.board_id} onChange={(e) => setBlockForm({ ...blockForm, board_id: e.target.value })}>
                    <option value="">Independent schools</option>
                    {seatBlocks.filter((b) => b.board_id).map((b) => <option key={b.board_id} value={b.board_id}>{b.board_name}</option>)}
                  </select>
                  <input data-testid="seat-block-count" className="input-base text-xs text-right" style={{ width: "6.5rem", flex: "0 0 auto" }}
                    type="number" min="1" placeholder="Seats" value={blockForm.count}
                    onChange={(e) => setBlockForm({ ...blockForm, count: e.target.value })} />
                  <input data-testid="seat-block-note" className="input-base text-xs" style={{ width: "14rem", flex: "0 0 auto" }}
                    placeholder="Note (e.g. invoice #)" value={blockForm.note}
                    onChange={(e) => setBlockForm({ ...blockForm, note: e.target.value })} />
                  <label className="flex items-center gap-1.5 text-xs text-[#3F6212] cursor-pointer" title="Demo/trial block — works like a normal block, clearly labelled">
                    <input data-testid="seat-block-demo" type="checkbox" checked={blockForm.demo}
                      onChange={(e) => setBlockForm({ ...blockForm, demo: e.target.checked })} />
                    Demo
                  </label>
                  {blockForm.demo && (
                    <input data-testid="seat-block-expiry" className="input-base text-xs" style={{ width: "9.5rem", flex: "0 0 auto" }}
                      type="date" title="Optional expiry — after this date the demo seats stop counting"
                      value={blockForm.expires_on}
                      onChange={(e) => setBlockForm({ ...blockForm, expires_on: e.target.value })} />
                  )}
                  <button data-testid="seat-block-add" className="btn-secondary text-xs" onClick={addSeatBlock}>+ Add seat block</button>
                </div>
              </div>
            )}
          </div>
        )}

        {sendDlg && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="send-email-modal" onClick={() => setSendDlg(null)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="font-bold mb-3">Email {sendDlg.inv.kind === "quote" ? "quote" : "invoice"} {sendDlg.inv.invoice_number} (PDF attached)</div>
              <div className="grid sm:grid-cols-2 gap-x-3">
                <div>
                  <label className="text-xs font-semibold text-[#000000]">To</label>
                  <input data-testid="send-to-input" className="input-base w-full mb-2" placeholder="customer@email.com"
                    value={sendDlg.to} onChange={(e) => setSendDlg({ ...sendDlg, to: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#000000]">CC <span className="font-normal text-[#000000]">(optional, comma-separate for multiple)</span></label>
                  <input data-testid="send-cc-input" className="input-base w-full mb-2" placeholder="colleague@email.com"
                    value={sendDlg.cc} onChange={(e) => setSendDlg({ ...sendDlg, cc: e.target.value })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                <input type="checkbox" data-testid="send-bcc-me" checked={sendDlg.bcc_me}
                  onChange={(e) => setSendDlg({ ...sendDlg, bcc_me: e.target.checked })} />
                BCC me a copy (lands in your own inbox)
              </label>
              <label className="text-xs font-semibold text-[#000000]">Subject</label>
              <input data-testid="send-subject-input" className="input-base w-full mb-2"
                value={sendDlg.subject} onChange={(e) => setSendDlg({ ...sendDlg, subject: e.target.value })} />
              <label className="text-xs font-semibold text-[#000000]">Your message <span className="font-normal text-[#000000]">(this is exactly what will be sent)</span></label>
              <div className="flex flex-wrap items-center gap-1.5 my-1.5" data-testid="snippets-bar">
                <span className="text-[10px] font-bold uppercase text-[#000000]">Snippets:</span>
                {snippets.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 text-xs rounded-full border border-[#C4C8CE] bg-[#F4F4F5] pl-2.5 pr-1 py-0.5">
                    <button type="button" className="font-semibold text-[#000000] cursor-pointer bg-transparent border-0 p-0"
                      title={s.text.slice(0, 200)} data-testid={`snippet-insert-${s.id}`}
                      onClick={() => insertSnippet(s.text)}>{s.title}</button>
                    <button type="button" className="text-[#000000] hover:text-[#B91C1C] bg-transparent border-0 cursor-pointer px-0.5"
                      title="Delete snippet" data-testid={`snippet-delete-${s.id}`} onClick={() => deleteSnippet(s)}>×</button>
                  </span>
                ))}
                {!snippets.length && <span className="text-xs text-[#000000] italic">none yet — write something, then save it for reuse</span>}
                <button type="button" className="btn-secondary text-xs py-0.5 px-2" onClick={saveSnippet} data-testid="save-snippet-btn">+ Save as snippet</button>
              </div>
              <textarea ref={msgRef} data-testid="send-message-input" className="input-base w-full mb-1 text-sm leading-relaxed"
                style={{ minHeight: "340px", resize: "vertical" }}
                value={sendDlg.message} onChange={(e) => setSendDlg({ ...sendDlg, message: e.target.value })} />
              <div className="text-[11px] text-[#000000] mb-3">
                Edit it like a normal email — greeting, body and signature are all yours to change. The {sendDlg.inv.kind === "quote" ? "quote" : "invoice"} PDF is always attached.{sendDlg.inv.kind === "quote" ? " Accept button(s) are added automatically so the customer can accept in one click." : ""} Blank line = new paragraph. Drag the corner to enlarge.
              </div>
              <div className="text-xs font-semibold text-[#000000] mb-1">Send from</div>
              <label className="flex items-center gap-2 text-sm mb-1 cursor-pointer">
                <input type="radio" name="invoice-sender" checked={!sendDlg.from_email}
                  onChange={() => setSendDlg({ ...sendDlg, from_email: "" })} data-testid="sender-default" />
                Default sender (The Self-Agency Project)
              </label>
              {(identity?.sender_emails || []).map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm mb-1 cursor-pointer">
                  <input type="radio" name="invoice-sender" checked={sendDlg.from_email === s}
                    onChange={() => setSendDlg({ ...sendDlg, from_email: s })} data-testid={`sender-option-${s}`} />
                  {s}
                </label>
              ))}
              <div className="text-[11px] text-[#000000] mt-1 mb-3">Manage sender addresses in Business identity. New domains must be verified in Resend before they can send.</div>
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={() => setSendDlg(null)}>Cancel</button>
                <button className="btn-primary" onClick={doSend} data-testid="send-email-confirm">Send</button>
              </div>
            </div>
          </div>
        )}
        {chargeDlg && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="qr-charge-modal" onClick={() => setChargeDlg(null)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 text-center max-h-[88vh] overflow-y-auto overscroll-contain" onClick={(e) => e.stopPropagation()}>
              {!chargeDlg.charge ? (
                <>
                  <div className="font-bold mb-1 text-left">Charge a customer</div>
                  <p className="text-xs text-[#000000] mb-3 text-left">Enter the amount — a QR code appears. The customer scans it and pays by card, Apple Pay or Google Pay on their own phone.</p>
                  {chargePresets?.length > 0 && (
                    <div className="mb-3 text-left" data-testid="charge-presets">
                      <div className="text-xs font-semibold text-[#000000] mb-1.5">Quick picks from your catalog</div>
                      <div className="flex flex-wrap gap-1.5">
                        {chargePresets.map((pr, i) => (
                          <button key={i} type="button" data-testid={`charge-preset-${i}`}
                            className={`text-xs px-2.5 py-1.5 rounded-full border cursor-pointer transition-colors ${chargeDlg.description === pr.label && Math.round(parseFloat(chargeDlg.amount || "0") * 100) === pr.cents ? "bg-[#0A0D14] text-white border-[#0A0D14]" : "bg-white border-[#C4C8CE] hover:bg-[#F4F4F5]"}`}
                            onClick={() => setChargeDlg({ ...chargeDlg, amount: (pr.cents / 100).toFixed(2), description: pr.label })}>
                            <b>{fmt(pr.cents)}</b> · {pr.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <label className="text-xs font-semibold text-[#000000] block text-left">Amount (CAD)</label>
                  <input data-testid="charge-amount-input" type="number" min="1" step="0.01" className="input-base w-full mb-2 text-lg font-bold"
                    placeholder="60.00" value={chargeDlg.amount} autoFocus
                    onChange={(e) => setChargeDlg({ ...chargeDlg, amount: e.target.value })} />
                  <label className="text-xs font-semibold text-[#000000] block text-left">What's it for? <span className="font-normal text-[#000000]">(optional)</span></label>
                  <input data-testid="charge-desc-input" className="input-base w-full mb-4" placeholder="Drop-in session"
                    value={chargeDlg.description} onChange={(e) => setChargeDlg({ ...chargeDlg, description: e.target.value })} />
                  <div className="flex justify-end gap-2">
                    <button className="btn-secondary" onClick={() => setChargeDlg(null)}>Cancel</button>
                    <button className="btn-primary" data-testid="charge-create-btn"
                      disabled={!(parseFloat(chargeDlg.amount) >= 1)} onClick={createCharge}>Show QR code</button>
                  </div>
                </>
              ) : chargeDlg.status === "paid" ? (
                <div className="py-6" data-testid="charge-paid-state">
                  <div className="text-5xl mb-2">✅</div>
                  <div className="text-xl font-extrabold text-[#0A6B2C]">PAID — {fmt(chargeDlg.charge.amount_cents)}</div>
                  <p className="text-sm text-[#000000] mt-1">{chargeDlg.description || "In-person payment"} · money is in your Stripe account</p>
                  <div className="flex justify-center gap-2 mt-5">
                    <button className="btn-secondary" onClick={() => setChargeDlg(null)}>Done</button>
                    <button className="btn-primary" onClick={() => setChargeDlg({ amount: "", description: "" })} data-testid="charge-new-btn">New charge</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="font-extrabold text-2xl mb-1">{fmt(chargeDlg.charge.amount_cents)} CAD</div>
                  <p className="text-xs text-[#000000] mb-3">{chargeDlg.description || "In-person payment"} — ask the customer to scan with their phone camera</p>
                  <div className="flex justify-center mb-3" data-testid="charge-qr">
                    <QRCodeSVG value={chargeDlg.charge.url} size={220} level="M" includeMargin />
                  </div>
                  <div className="flex items-center justify-center gap-1.5 text-xs text-[#000000] mb-3">
                    <span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" /> Waiting for payment…
                  </div>
                  <div className="flex justify-center gap-2">
                    <button className="btn-secondary text-xs" onClick={async () => {
                      try { await navigator.clipboard.writeText(chargeDlg.charge.url); toast.success("Payment link copied — text or email it"); } catch {}
                    }} data-testid="charge-copy-link">Copy payment link</button>
                    <button className="btn-secondary text-xs" onClick={() => setChargeDlg(null)}>Close</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {convDlg && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="convert-options-modal" onClick={() => setConvDlg(null)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
              <div className="font-bold mb-1">Which option did they choose?</div>
              <p className="text-xs text-[#000000] mb-3">Quote {convDlg.inv.invoice_number} presents options — the invoice will keep only the chosen one and note it for the customer.</p>
              {(convDlg.inv.items || []).map((it, i) => {
                const line = (it.quantity || 0) * (it.unit_price_cents || 0);
                const rate = (convDlg.inv.tax_lines || []).reduce((s, l) => s + (l.rate || 0), 0) || 0.05;
                const tax = it.taxable === false ? 0 : Math.round(line * rate);
                return (
                  <label key={i} className="flex items-start gap-2 text-sm mb-2 cursor-pointer rounded-lg border border-[#E4E4E7] p-2 hover:bg-[#F4F4F5]">
                    <input type="radio" name="chosen-option" className="mt-0.5" checked={convDlg.idx === i}
                      onChange={() => setConvDlg({ ...convDlg, idx: i })} data-testid={`chosen-option-${i}`} />
                    <span className="min-w-0">
                      <span className="font-semibold block">Option {i + 1} — {it.name || it.description || "…"}</span>
                      <span className="text-xs text-[#000000]">{fmt(line)}{tax ? ` + tax ${fmt(tax)} = ${fmt(line + tax)}` : " (tax exempt)"}</span>
                    </span>
                  </label>
                );
              })}
              <div className="flex justify-end gap-2 mt-3">
                <button className="btn-secondary" onClick={() => setConvDlg(null)}>Cancel</button>
                <button className="btn-primary" onClick={() => convert(convDlg.inv.id, convDlg.idx)} data-testid="convert-options-confirm">Convert to invoice</button>
              </div>
            </div>
          </div>
        )}
        {histDlg && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="comms-history-modal" onClick={() => setHistDlg(null)}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="font-bold mb-3">Sent history — {histDlg.inv.kind === "quote" ? "quote" : "invoice"} {histDlg.inv.invoice_number}</div>
              {histDlg.rows.length === 0 ? (
                <div className="text-sm text-[#000000]" data-testid="comms-history-empty">Nothing sent yet for this document.</div>
              ) : histDlg.rows.map((r) => (
                <div key={r.id} className="border-b border-[#EDEEF0] py-2 text-sm" data-testid="comms-history-row">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.status === "sent" ? "bg-[#DCFCE7] text-[#166534]" : r.status === "skipped" ? "bg-[#F4F4F5] text-[#000000]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>
                      {r.status === "sent" ? "Sent ✓" : r.status === "skipped" ? "Skipped" : "Failed ✗"}
                    </span>
                    <span className="text-[11px] uppercase text-[#000000]">{r.channel}</span>
                    <span className="text-xs text-[#000000] ml-auto">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-[#000000] mt-0.5 break-all">To {r.to}{r.cc ? ` · cc ${r.cc}` : ""}{r.bcc ? ` · bcc ${r.bcc}` : ""} · from {r.from_email}</div>
                  {r.error && <div className="text-xs text-[#B91C1C] mt-0.5">{r.error}</div>}
                </div>
              ))}
              <div className="flex justify-end mt-3">
                <button className="btn-secondary" onClick={() => setHistDlg(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
        {viewDoc && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="pdf-view-modal" onClick={closeView}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl flex flex-col" style={{ height: "90vh" }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-2 border-b border-[#E4E4E7]">
                <div className="font-bold text-sm">{viewDoc.kind === "quote" ? "Quote" : "Invoice"} {viewDoc.number}</div>
                <div className="flex items-center gap-2">
                  <button onClick={printView} className="btn-secondary text-xs" data-testid="pdf-view-print">Print</button>
                  <a href={viewDoc.url} download={`${viewDoc.kind}-${viewDoc.number}.pdf`} className="btn-secondary text-xs" data-testid="pdf-view-download">Download</a>
                  <button data-testid="pdf-view-close" className="btn-ghost" onClick={closeView} title="Close"><X size={18} weight="bold" /></button>
                </div>
              </div>
              <PdfCanvasViewer blob={viewDoc.blob} url={viewDoc.url} />
            </div>
          </div>
        )}
        {builder && (
          <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" data-testid="document-builder-modal">
            <div className="max-w-6xl mx-auto my-6">
              <DocumentBuilder
                kind={builder.kind}
                identity={identity}
                products={products}
                customers={customers}
                initial={builder.initial}
                onDone={async () => { setBuilder(null); await load(); onRefresh && onRefresh(); }}
                onCancel={() => setBuilder(null)}
              />
            </div>
          </div>
        )}

        {payInv && (
          <RecordPaymentModal invoice={payInv} onClose={() => setPayInv(null)}
            onChanged={async () => { setPayInv(null); await load(); }} />
        )}

        {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}

        {(moneyFilter || customerFilter) && (
          <div className="mb-2 flex items-center gap-2 text-xs flex-wrap" data-testid="money-filter-chip">
            {moneyFilter && (
              <span className="chip font-bold" style={{ background: "#CCFF00", color: "#000000", border: "1px solid #A3B800" }}>
                Showing {moneyFilter} invoices only
              </span>
            )}
            {customerFilter && (
              <span className="chip font-bold" data-testid="customer-filter-chip" style={{ background: "#EDE9FE", color: "#5B21B6", border: "1px solid #C4B5FD" }}>
                Customer: {customerFilter}
              </span>
            )}
            <button className="btn-ghost underline underline-offset-2 text-[#000000]" onClick={() => { setMoneyFilter(null); setCustomerFilter(null); }} data-testid="money-filter-clear">clear</button>
          </div>
        )}

        <div className="sm:hidden text-[10px] text-[#000000] mb-1 text-right">← swipe the table sideways for more →</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#000000] uppercase text-xs tracking-[0.1em]">
                <th className="p-2">#</th><th className="p-2">Kind</th><th className="p-2">Customer</th>
                <th className="p-2">Total</th><th className="p-2">Paid</th><th className="p-2 hidden sm:table-cell">Status</th><th className="p-2 hidden md:table-cell">Issued</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.filter((inv) => {
                if (customerFilter && (inv.customer_name || "") !== customerFilter) return false;
                if (!moneyFilter) return true;
                if ((inv.kind || "invoice") !== "invoice" || inv.status === "draft") return false;
                if (moneyFilter === "collected") return (inv.paid_cents || 0) > 0;
                if (moneyFilter === "outstanding") return (inv.balance_cents ?? inv.total_cents) > 0;
                if (moneyFilter === "overdue") return !!inv.overdue;
                return true;
              }).map(inv => {
                const kind = inv.kind || "invoice";
                return (
                  <tr key={inv.id} className="border-t border-[#C4C8CE]" data-testid={`invoice-row-${inv.id}`}>
                    <td className="p-2 font-mono text-[#3F6212] font-bold">{inv.invoice_number}</td>
                    <td className="p-2">
                      <span className="chip text-[10px] font-bold uppercase" style={kind === "quote"
                        ? { background: "rgba(0,184,212,0.12)", color: "#007A8F", border: "1px solid #00B8D4" }
                        : { background: "rgba(204,255,0,0.25)", color: "#3F6212", border: "1px solid #A3B800" }}>
                        {kind}
                      </span>
                      {inv.seat_block_auto?.status === "created" && (
                        <span className="chip text-[10px] font-bold ml-1" data-testid={`seat-grant-chip-${inv.id}`}
                          style={{ background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" }}
                          title="A prepaid seat block was automatically created from this invoice's seats line">
                          ✓ {inv.seat_block_auto.count} seats granted
                        </span>
                      )}
                      {inv.seat_block_auto?.status === "no_board_match" && (
                        <span className="chip text-[10px] font-bold ml-1" data-testid={`seat-grant-warn-${inv.id}`}
                          style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" }}
                          title="This invoice has a seats line but the customer name didn't match any school board — add the seat block manually in Prepaid seat blocks">
                          ⚠ seats: no board match
                        </span>
                      )}
                    </td>
                    <td className="p-2">{inv.customer_name}</td>
                    <td className="p-2 font-bold">
                      {inv.options_mode
                        ? <span className="chip text-[10px] font-bold" data-testid={`options-chip-${inv.id}`}
                            style={{ background: "#E0F2FE", color: "#075985", border: "1px solid #7DD3FC" }}
                            title="Options quote — each line is priced individually, no combined total">
                            {(inv.items || []).length} options
                          </span>
                        : fmt(inv.total_cents)}
                    </td>
                    <td className="p-2" data-testid={`invoice-paid-${inv.id}`}>
                      {kind === "invoice" ? (
                        (() => { const chip = payChip(inv); return (
                          <div>
                            <span className="chip text-[10px] font-bold whitespace-nowrap"
                              style={{ background: chip.bg, color: chip.color, border: `1px solid ${chip.border}` }}
                              title={inv.due_date ? `Due ${inv.due_date} · paid ${fmt(inv.paid_cents)} of ${fmt(inv.total_cents)}` : undefined}>
                              {chip.label}
                            </span>
                            {inv.due_date && inv.status !== "draft" && inv.payment_status !== "paid" && (
                              <div className="text-[10px] font-bold mt-0.5" style={{ color: "#000000" }} data-testid={`due-days-${inv.id}`}>
                                {dueLabel(inv.due_date)} · {inv.due_date}
                              </div>
                            )}
                          </div>
                        ); })()
                      ) : inv.valid_until && inv.status !== "draft" ? (
                        <span className="text-[10px] font-bold whitespace-nowrap" style={{ color: "#000000" }} data-testid={`valid-days-${inv.id}`}>
                          {validLabel(inv.valid_until)} · {inv.valid_until}
                        </span>
                      ) : <span className="text-xs text-[#000000]">—</span>}
                    </td>
                    <td className="p-2 text-xs text-[#000000] hidden sm:table-cell">
                      {inv.status === "draft"
                        ? <span className="chip text-[10px] font-bold uppercase" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FBBF24" }}>draft</span>
                        : inv.status === "converted"
                          ? <span className="text-[#007A8F] font-semibold">converted</span>
                          : inv.status === "sent"
                            ? <span className="chip text-[10px] font-bold uppercase" style={{ background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" }} data-testid={`status-sent-${inv.id}`}>sent</span>
                            : (inv.status || "issued")}
                    </td>
                    <td className="p-2 text-[#000000] hidden md:table-cell">{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td className="p-2">
                      <div className="grid items-center justify-items-center" style={{ gridTemplateColumns: "repeat(9, 32px)" }}>
                      {/* Slot 1: payments ($) — invoices only */}
                      {inv.status !== "draft" && kind === "invoice" && inv.payment_status !== "paid" ? (
                        <button data-testid={`record-payment-${inv.id}`} className="btn-ghost p-1.5 text-[#166534]"
                          title="Record a payment (cheque / EFT / wire / cash)" onClick={() => setPayInv(inv)}>
                          <CurrencyDollar size={16} weight="bold" />
                        </button>
                      ) : inv.status !== "draft" && kind === "invoice" && (inv.payments || []).length > 0 ? (
                        <button data-testid={`view-payments-${inv.id}`} className="btn-ghost p-1.5 text-[#000000] inline-flex items-center"
                          title="View recorded payments" onClick={() => setPayInv(inv)}>
                          <CurrencyDollar size={16} weight="bold" /><span className="text-[10px]">{inv.payments.length}</span>
                        </button>
                      ) : <span aria-hidden="true" />}
                      {/* Slot 2: view on screen — always */}
                      <button data-testid={inv.status === "draft" ? `invoice-view-draft-${inv.id}` : `invoice-view-${inv.id}`}
                        className="btn-ghost p-1.5 text-[#0E7490]"
                        title={inv.status === "draft" ? "Preview this draft on screen" : `View this ${kind} on screen without downloading`}
                        onClick={() => viewPdf(inv)}>
                        <Eye size={16} weight="bold" />
                      </button>
                      {/* Slot 2b: view the original quote this invoice was converted from */}
                      {inv.quote_snapshot ? (
                        <button data-testid={`invoice-quote-snapshot-${inv.id}`} className="btn-ghost p-1.5 text-[#7C3AED] relative"
                          title="View the original quote this invoice was converted from"
                          onClick={() => viewQuoteSnapshot(inv)}>
                          <Eye size={16} weight="bold" />
                          <span className="absolute font-black" style={{ fontSize: "10px", top: "0px", right: "-1px", lineHeight: 1, background: "#EDE9FE", borderRadius: "3px", padding: "0 1px" }}>Q</span>
                        </button>
                      ) : <span aria-hidden="true" />}
                      {/* Slot 3: download PDF */}
                      {inv.status !== "draft" ? (
                        <a data-testid={`invoice-download-${inv.id}`} href={`${API_BASE}/admin/invoices/${inv.id}/pdf`} target="_blank" rel="noopener noreferrer"
                          title="Download the PDF" className="btn-ghost p-1.5 text-[#3F6212] inline-flex items-center">
                          <DownloadSimple size={16} weight="bold" />
                        </a>
                      ) : <span aria-hidden="true" />}
                      {/* Slot 4: email */}
                      {inv.status !== "draft" ? (
                        <button data-testid={`invoice-email-${inv.id}`} className="btn-ghost p-1.5 text-[#1E40AF]"
                          title={inv.last_sent_to ? `Re-send — last sent to ${inv.last_sent_to}` : "Email the PDF to the customer"}
                          onClick={() => sendEmail(inv)}>
                          <PaperPlaneTilt size={16} weight="bold" />
                        </button>
                      ) : <span aria-hidden="true" />}
                      {/* Slot 4b: email history */}
                      {inv.status !== "draft" ? (
                        <button data-testid={`invoice-history-${inv.id}`} className="btn-ghost p-1.5 text-[#000000]"
                          title="Sent-email history for this document"
                          onClick={() => openHistory(inv)}>
                          <ClockCounterClockwise size={16} weight="bold" />
                        </button>
                      ) : <span aria-hidden="true" />}
                      {/* Slot 5: edit draft / revise */}
                      {inv.status === "draft" ? (
                        <button data-testid={`edit-draft-${inv.id}`} className="btn-ghost p-1.5 text-[#92400E] font-bold"
                          title="Open this draft to keep editing — same number, nothing is issued yet"
                          onClick={() => setBuilder({ kind, initial: {
                            edit_id: inv.id,
                            number: inv.invoice_number,
                            customer_id: inv.customer_id || null,
                            customer_name: inv.customer_name,
                            customer_address: inv.customer_address,
                            customer_email: inv.customer_email || "",
                            customer_phone: inv.customer_phone || "",
                            notes: inv.notes || "",
                            tax_province: inv.tax_province,
                            terms_days: inv.terms_days ?? "",
                            terms_text: inv.terms_text || "",
                            revision_of: inv.revision_of || null,
                            seat_ledger_board_id: inv.seat_ledger_board_id || null,
                            options_mode: !!inv.options_mode,
                            items: (inv.items || []).map((it) => ({
                              name: it.name || it.description || "",
                              description: it.name ? (it.description || "") : "",
                              quantity: it.quantity,
                              unit_price_cents: it.unit_price_cents,
                              product_id: it.product_id || null,
                              variation_id: it.variation_id || null,
                              taxable: it.taxable !== false,
                            })),
                          } })}>
                          <PencilSimple size={16} weight="bold" />
                        </button>
                      ) : inv.status !== "converted" ? (
                        <button data-testid={`revise-${kind}-${inv.id}`} className="btn-ghost p-1.5 text-[#92400E]"
                          title={`Revise this ${kind} — opens pre-filled, saves as a new number (e.g. ${inv.invoice_number}a)`}
                          onClick={() => {
                            setBuilder({ kind, initial: {
                              revision_of: inv.invoice_number,
                              customer_id: inv.customer_id || null,
                              customer_name: inv.customer_name,
                              customer_address: inv.customer_address,
                              customer_email: inv.customer_email || "",
                              customer_phone: inv.customer_phone || "",
                              notes: inv.notes || "",
                              tax_province: inv.tax_province,
                              terms_days: inv.terms_days ?? "",
                              terms_text: inv.terms_text || "",
                              options_mode: !!inv.options_mode,
                              items: (inv.items || []).map((it) => ({
                                name: it.name || it.description || "",
                                description: it.name ? (it.description || "") : "",
                                quantity: it.quantity,
                                unit_price_cents: it.unit_price_cents,
                                product_id: it.product_id || null,
                                variation_id: it.variation_id || null,
                              })),
                            } });
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}>
                          <PencilSimple size={16} weight="bold" />
                        </button>
                      ) : <span aria-hidden="true" />}
                      {/* Slot 6: convert quote → invoice */}
                      {inv.status !== "draft" && kind === "quote" && !inv.converted_invoice_id ? (
                        <button data-testid={`convert-quote-${inv.id}`} className="btn-ghost p-1.5 text-[#007A8F]"
                          title="Convert this quote into an invoice"
                          onClick={() => inv.options_mode ? setConvDlg({ inv, idx: 0 }) : convert(inv.id)}>
                          <ArrowRight size={16} weight="bold" />
                        </button>
                      ) : <span aria-hidden="true" />}
                      {/* Slot 7: delete — always last */}
                      <button data-testid={`delete-doc-${inv.id}`} className="btn-ghost p-1.5 text-[#FF5100]"
                        title={`Delete this ${kind} — an Undo button appears for 10 seconds`}
                        onClick={() => deleteDoc(inv)}>
                        <Trash size={16} weight="bold" />
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {list.length === 0 && <div className="text-[#000000] text-sm p-4 text-center">No documents yet. Create your first invoice or quote.</div>}
        </div>
        <p className="text-xs text-[#000000] mt-3">
          Numbers are date-based and shared across quotes and invoices: YYYYMMDD + 4-digit daily count (revisions add a letter, e.g. …0004c). Documents include your business identity, logo and GST/HST number.
        </p>
      </div>
    </div>
  );
}

function MoneyStrip({ list, filter, onFilter }) {
  const year = new Date().getFullYear();
  const invs = list.filter((i) => (i.kind || "invoice") === "invoice" && i.status !== "draft" && new Date(i.created_at).getFullYear() === year);
  const invoiced = invs.reduce((s, i) => s + i.total_cents, 0);
  const collected = invs.reduce((s, i) => s + (i.paid_cents || 0), 0);
  const outstanding = invs.reduce((s, i) => s + (i.balance_cents ?? i.total_cents), 0);
  const overdue = invs.filter((i) => i.overdue).reduce((s, i) => s + (i.balance_cents || 0), 0);
  const items = [
    { key: "invoiced", label: `Invoiced ${year}`, v: invoiced, color: "#000000" },
    { key: "collected", label: "Collected", v: collected, color: "#166534" },
    { key: "outstanding", label: "Outstanding", v: outstanding, color: "#92400E" },
    { key: "overdue", label: "Overdue", v: overdue, color: "#B91C1C" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="money-strip">
      {items.map((it) => {
        const active = filter === it.key || (it.key === "invoiced" && !filter);
        return (
          <button key={it.key} type="button"
            onClick={() => onFilter(it.key === "invoiced" ? null : (filter === it.key ? null : it.key))}
            className="card !p-3 text-left cursor-pointer transition-shadow"
            style={{ boxShadow: active && filter ? "inset 0 0 0 3px #CCFF00" : undefined }}
            title={it.key === "invoiced" ? "Show all documents" : `Click to show only ${it.key} invoices`}>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#000000]">{it.label}</div>
            <div className="font-display font-extrabold text-xl" style={{ color: it.color }} data-testid={`money-${it.key}`}>{fmt(it.v)}</div>
          </button>
        );
      })}
    </div>
  );
}

function StripeSalesCard() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const loadLedger = () => api.get("/admin/stripe/ledger").then((r) => setData(r.data))
    .catch(() => setData({ transactions: [], events: [], summary: {} }));
  useEffect(() => {
    if (open && !data) loadLedger();
  }, [open, data]);

  const refund = async (t) => {
    if (!window.confirm(`Refund ${fmt(t.amount_cents)} to ${t.buyer_name || "the buyer"} and remove their course access?`)) return;
    try {
      await api.post("/admin/stripe/refund", { transaction_id: t.id });
      toast.success("Refund issued — course access removed");
      loadLedger();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const txChip = (t) => {
    if (t.disputed) return { label: "disputed", bg: "#F3E8FF", color: "#6B21A8", border: "#D8B4FE" };
    if (t.payment_status === "paid") return { label: "paid", bg: "#DCFCE7", color: "#166534", border: "#86EFAC" };
    if (t.payment_status === "refunded") return { label: "refunded", bg: "#FEE2E2", color: "#B91C1C", border: "#FCA5A5" };
    if (t.payment_status === "partially_refunded") return { label: "part-refunded", bg: "#FEF3C7", color: "#92400E", border: "#FDE68A" };
    return { label: t.payment_status || "pending", bg: "#F4F4F5", color: "#000000", border: "#D4D4D8" };
  };

  return (
    <div className="card" data-testid="stripe-sales-card">
      <button type="button" className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" onClick={() => setOpen((o) => !o)} data-testid="stripe-sales-toggle">
        <div className="flex items-center gap-2">
          <Receipt size={22} weight="duotone" color="#635BFF" />
          <div className="text-left">
            <h3 className="text-lg font-bold font-display">Public sales (Stripe)</h3>
            <p className="text-xs text-[#000000]">
              {data?.summary
                ? `${data.summary.paid_count || 0} paid · ${fmt(data.summary.gross_cents)} gross · ${data.summary.refunded_count || 0} refunded${data.summary.disputed_count ? ` · ${data.summary.disputed_count} disputed` : ""}`
                : "Card sales, refunds & chargebacks auto-recorded from Stripe webhooks"}
            </p>
          </div>
        </div>
        {open ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
      </button>
      {open && data && (
        <div className="mt-4">
          <div className="sm:hidden text-[10px] text-[#000000] mb-1 text-right">← swipe the table sideways for more →</div>
          <div className="overflow-x-auto">
          {data.transactions.length === 0 ? (
            <div className="text-[#000000] text-sm italic">No public purchases yet — Stripe sales will appear here automatically (test card: 4242 4242 4242 4242).</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#000000] uppercase text-xs tracking-[0.1em]">
                  <th className="p-2">Date</th><th className="p-2">Buyer</th><th className="p-2">Course</th>
                  <th className="p-2">Amount</th><th className="p-2">Status</th><th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t) => {
                  const chip = txChip(t);
                  return (
                    <tr key={t.id} className="border-t border-[#C4C8CE]" data-testid={`stripe-txn-${t.id}`}>
                      <td className="p-2 text-[#000000]">{new Date(t.created_at).toLocaleDateString()}</td>
                      <td className="p-2">{t.buyer_name || "—"}</td>
                      <td className="p-2">{t.course_title || "—"}</td>
                      <td className="p-2 font-bold">{fmt(t.amount_cents)}{t.refunded_cents ? <span className="text-xs text-[#B91C1C]"> (−{fmt(t.refunded_cents)})</span> : null}</td>
                      <td className="p-2"><span className="chip text-[10px] font-bold" style={{ background: chip.bg, color: chip.color, border: `1px solid ${chip.border}` }}>{chip.label}</span></td>
                      <td className="p-2">
                        {t.payment_status === "paid" && t.stripe_payment_intent_id && (
                          <button type="button" className="btn-secondary text-xs py-0.5 px-2" title="Issue a full Stripe refund and remove course access"
                            onClick={() => refund(t)} data-testid={`refund-txn-${t.id}`}>
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

function RecordPaymentModal({ invoice, onClose, onChanged }) {
  const balance = invoice.balance_cents ?? invoice.total_cents;
  const [amount, setAmount] = useState((balance / 100).toFixed(2));
  const [method, setMethod] = useState("eft");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fullyPaid = invoice.payment_status === "paid";

  const submit = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || cents <= 0) { setErr("Enter a payment amount"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/invoices/${invoice.id}/payments`, {
        amount_cents: cents, method,
        reference: reference.trim() || null, note: note.trim() || null,
        received_on: receivedOn,
      });
      toast.success(data.payment_status === "paid"
        ? `Invoice ${invoice.invoice_number} is fully paid 🎉`
        : `Payment recorded — ${fmt(data.balance_cents)} still due`);
      await onChanged();
    } catch (e2) { setErr(formatApiError(e2)); setBusy(false); }
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Remove this recorded payment? The invoice balance will be recalculated.")) return;
    try {
      await api.delete(`/admin/invoices/${invoice.id}/payments/${pid}`);
      toast.success("Payment removed");
      await onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="record-payment-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-[#E8EAED] p-5 text-[#000000] max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold font-display flex items-center gap-2">
            <CurrencyDollar size={20} weight="bold" color="#166534" /> {invoice.invoice_number}
          </h3>
          <button className="btn-ghost p-1" onClick={onClose} data-testid="payment-modal-close"><X size={18} weight="bold" /></button>
        </div>
        <p className="text-sm text-[#000000] mb-3">
          {invoice.customer_name} — total {fmt(invoice.total_cents)}, paid {fmt(invoice.paid_cents || 0)},{" "}
          <strong className={balance > 0 ? "text-[#B91C1C]" : "text-[#166534]"}>{fmt(balance)} due</strong>
        </p>

        {(invoice.payments || []).length > 0 && (
          <div className="mb-4 flex flex-col gap-1.5">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#000000]">Payments received</div>
            {invoice.payments.map((p) => (
              <div key={p.id} className="flex items-center gap-2 rounded-lg bg-white border border-[#C4C8CE] px-3 py-1.5 text-sm" data-testid={`payment-row-${p.id}`}>
                <span className="font-bold">{fmt(p.amount_cents)}</span>
                <span className="chip text-[10px] uppercase font-bold" style={{ background: "#F4F4F5", color: "#000000", border: "1px solid #D4D4D8" }}>{p.method}</span>
                <span className="text-xs text-[#000000] truncate">{p.received_on}{p.reference ? ` · ${p.reference}` : ""}</span>
                <button className="btn-ghost p-1 ml-auto" title="Remove this payment" onClick={() => removePayment(p.id)} data-testid={`delete-payment-${p.id}`}>
                  <Trash size={13} color="#FF5100" />
                </button>
              </div>
            ))}
          </div>
        )}

        {fullyPaid ? (
          <div className="text-sm font-bold text-[#166534] rounded-lg bg-[#DCFCE7] border border-[#86EFAC] px-3 py-2">This invoice is fully paid.</div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-2.5">
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] uppercase tracking-[0.12em] text-[#000000] flex flex-col gap-1">Amount (CAD)
                <input data-testid="payment-amount" className="input-base" type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </label>
              <label className="text-[10px] uppercase tracking-[0.12em] text-[#000000] flex flex-col gap-1">Method
                <select data-testid="payment-method" className="input-base" value={method} onChange={(e) => setMethod(e.target.value)}>
                  <option value="eft">EFT / e-transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="wire">Wire</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] uppercase tracking-[0.12em] text-[#000000] flex flex-col gap-1">Received on
                <input data-testid="payment-date" className="input-base" type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
              </label>
              <label className="text-[10px] uppercase tracking-[0.12em] text-[#000000] flex flex-col gap-1">Reference (cheque #…)
                <input data-testid="payment-reference" className="input-base" value={reference} onChange={(e) => setReference(e.target.value)} />
              </label>
            </div>
            <input data-testid="payment-note" className="input-base" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            {err && <div className="text-[#FF5100] text-sm">{err}</div>}
            <button type="submit" className="btn-primary" disabled={busy} data-testid="payment-submit">
              {busy ? "Recording…" : "Record payment"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function BusinessIdentityCard({ identity, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [taxRates, setTaxRates] = useState([]);
  const logoRef = useRef(null);
  useEffect(() => { setForm(identity); }, [identity]);
  useEffect(() => {
    if (open && taxRates.length === 0) {
      api.get("/admin/tax-rates").then((r) => setTaxRates(r.data || [])).catch(() => {});
    }
  }, [open, taxRates.length]);
  if (!identity) return null;

  const uploadLogo = async (f) => {
    if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await apiFetch(`${API_BASE}/admin/settings/upload-image`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      const d = await res.json();
      setForm(prev => ({ ...prev, logo_url: d.url }));
    } catch (e) { toast.error(e.message); }
    finally { setLogoBusy(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/business-identity", form);
      onSaved(data);
      toast.success("Business identity saved — it now stamps every invoice & quote.");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" data-testid="business-identity-card">
      <button type="button" className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" onClick={() => setOpen(o => !o)} data-testid="business-identity-toggle">
        <div className="flex items-center gap-2">
          <Buildings size={22} weight="duotone" color="#00B8D4" />
          <div className="text-left">
            <h3 className="text-lg font-bold font-display">Business identity</h3>
            <p className="text-xs text-[#000000]">{identity.business_name} · GST/HST {identity.gst_number || "—"} — stamped on every invoice &amp; quote</p>
          </div>
        </div>
        {open ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
      </button>
      {open && form && (
        <div className="mt-4 flex flex-col gap-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Business name
              <input data-testid="identity-name" className="input-base" value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
            </label>
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">GST/HST number
              <input data-testid="identity-gst" className="input-base" placeholder="123456789RT0001" value={form.gst_number || ""} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} />
            </label>
          </div>
          <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Address (comma-separated lines)
            <input data-testid="identity-address" className="input-base" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Email
              <input data-testid="identity-email" className="input-base" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Phone
              <input data-testid="identity-phone" className="input-base" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </label>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Invoice number prefix
              <input data-testid="identity-invoice-prefix" className="input-base" placeholder="INV" value={form.invoice_prefix || ""} onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value })} />
            </label>
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Quote number prefix
              <input data-testid="identity-quote-prefix" className="input-base" placeholder="QTE" value={form.quote_prefix || ""} onChange={(e) => setForm({ ...form, quote_prefix: e.target.value })} />
            </label>
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Payment terms (days)
              <input data-testid="identity-terms" className="input-base" type="number" min="0" max="365" placeholder="30" value={form.payment_terms_days ?? 30} onChange={(e) => setForm({ ...form, payment_terms_days: Number(e.target.value) })} />
            </label>
            <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Quote valid for (days)
              <input data-testid="identity-quote-valid" className="input-base" type="number" min="0" max="365" placeholder="30" value={form.quote_valid_days ?? 30} onChange={(e) => setForm({ ...form, quote_valid_days: Number(e.target.value) })} />
            </label>
          </div>
          <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Tax province (default for new documents)
            <select data-testid="identity-tax-province" className="input-base" value={form.tax_province || "AB"} onChange={(e) => setForm({ ...form, tax_province: e.target.value })}>
              {(taxRates.length ? taxRates : [{ code: "AB", label: "Alberta", lines: [{ name: "GST", rate: 0.05 }] }]).map((t) => (
                <option key={t.code} value={t.code}>{t.label} — {t.lines.map((l) => `${l.name} ${(l.rate * 100).toLocaleString()}%`).join(" + ")}</option>
              ))}
            </select>
          </label>
          <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Document footer note (optional)
            <input data-testid="identity-footer" className="input-base" placeholder="e.g. Payment due within 30 days. E-transfers to pay@…" value={form.footer_note || ""} onChange={(e) => setForm({ ...form, footer_note: e.target.value })} />
            <div className="mt-3">
              <label className="text-xs font-semibold text-[#000000]">Sender email addresses (one per line) — choices offered when emailing an invoice</label>
              <textarea data-testid="identity-sender-emails" className="input-base w-full" rows={3}
                placeholder={"info@selfagency.ca\nbilling@elevation650.com"}
                value={form.sender_emails_raw ?? (form.sender_emails || []).join("\n")}
                onChange={(e) => setForm({
                  ...form,
                  sender_emails_raw: e.target.value,
                  sender_emails: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                })} />
              <div className="text-[11px] text-[#000000] mt-1">Each address's domain must be verified in Resend before it can send — ask the agent to walk you through verifying a new domain (takes ~5 min with Cloudflare DNS).</div>
            </div>
          </label>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs uppercase tracking-[0.1em] text-[#000000]">Logo</span>
            {form.logo_url
              ? <img src={form.logo_url} alt="logo" className="h-10 w-auto max-w-[160px] object-contain rounded border border-[#C4C8CE] bg-white p-1" data-testid="identity-logo-preview" />
              : <span className="text-xs text-[#000000] italic">none — PDFs show your business name only</span>}
            <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid="identity-logo-file" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            <button type="button" className="btn-secondary text-sm" onClick={() => logoRef.current?.click()} disabled={logoBusy} data-testid="identity-logo-upload">
              {logoBusy ? "Uploading…" : (form.logo_url ? "Replace logo" : "Upload logo")}
            </button>
            {form.logo_url && <button type="button" className="btn-ghost text-sm text-[#FF5100]" onClick={() => setForm({ ...form, logo_url: "" })} data-testid="identity-logo-clear">Remove</button>}
          </div>
          <div className="flex gap-2">
            <button className="btn-primary" onClick={save} disabled={busy} data-testid="identity-save">{busy ? "Saving…" : "Save identity"}</button>
            <button className="btn-secondary" onClick={() => { setForm(identity); setOpen(false); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentBuilder({ kind, identity, products, customers, initial, onDone, onCancel }) {
  const [customer, setCustomer] = useState({
    customer_id: initial?.customer_id || null,
    customer_name: initial?.customer_name || "",
    customer_address: initial?.customer_address || "",
    customer_email: initial?.customer_email || "",
    customer_phone: initial?.customer_phone || "",
  });
  const [items, setItems] = useState(initial?.items || [{ name: "", description: "", quantity: 1, unit_price_cents: 0, taxable: true }]);
  const [optionsMode, setOptionsMode] = useState(!!initial?.options_mode);
  const [numberOverride, setNumberOverride] = useState("");
  const [autoNumber, setAutoNumber] = useState("");
  const [notes, setNotes] = useState(initial?.notes || "");
  // One footer line covers both docs (e.g. "Quotes good for 60 days. Payments due in 30 days.")
  // Day counts are parsed straight out of the text so due/valid dates always track it.
  const parseTermsDays = (text) => {
    const m = kind === "quote"
      ? String(text || "").match(/(?:good|valid)\s+for\s+(\d+)\s*day/i)
      : String(text || "").match(/(?:due\s+in|net)\s+(\d+)\s*day/i);
    return m ? parseInt(m[1], 10) : null;
  };
  const [termsText, setTermsText] = useState(
    initial?.terms_text || identity?.footer_note ||
    (kind === "quote" ? "Quotes good for 30 days." : "Payments due in 30 days."));
  const parsedDays = parseTermsDays(termsText);
  const termsDays = parsedDays ?? (kind === "quote" ? (identity?.quote_valid_days ?? 30) : (identity?.payment_terms_days ?? 30));
  const termsEndDate = new Date(Date.now() + termsDays * 86400000)
    .toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [taxRates, setTaxRates] = useState([]);
  const [province, setProvince] = useState((initial?.tax_province || identity?.tax_province || "AB").toUpperCase());
  const defaultTerms = identity?.payment_terms_days || 30;

  useEffect(() => {
    api.get("/admin/tax-rates").then((r) => setTaxRates(r.data || [])).catch(() => {});
  }, []);
  const taxLines = (taxRates.find((t) => t.code === province)?.lines) || [{ name: "GST", rate: 0.05 }];

  useEffect(() => {
    if (initial?.edit_id) { setAutoNumber(initial.number || ""); return; }
    setAutoNumber("");
    const rev = initial?.revision_of ? `&revision_of=${encodeURIComponent(initial.revision_of)}` : "";
    api.get(`/admin/invoices/next-number?kind=${kind}${rev}`).then(r => setAutoNumber(r.data.number)).catch(() => {});
    // eslint-disable-next-line
  }, [kind]);

  const setItem = (i, key, val) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const addItem = () => setItems((p) => [...p, { name: "", description: "", quantity: 1, unit_price_cents: 0, taxable: true }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const catalogOptions = [];
  (products || []).forEach((p) => {
    catalogOptions.push({ value: p.id, label: `${p.name} (${fmt(p.base_price_cents)})` });
    const kids = {};
    (p.variations || []).forEach((v) => { (kids[v.parent_id || "root"] = kids[v.parent_id || "root"] || []).push(v); });
    const walk = (key, depth) => (kids[key] || []).forEach((v) => {
      catalogOptions.push({ value: `${p.id}:${v.id}`, label: `${"\u00A0\u00A0\u00A0".repeat(depth + 1)}↳ ${v.label} (${fmt(v.price_cents)})` });
      walk(v.id, depth + 1);
    });
    walk("root", 0);
  });
  const addFromCatalog = (value) => {
    const [pid, vid] = value.split(":");
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    const v = vid ? (p.variations || []).find((x) => x.id === vid) : null;
    const line = {
      name: v ? `${p.name} — ${v.label}` : p.name,
      description: v ? (v.reason || p.description || "") : (p.description || ""),
      quantity: 1,
      unit_price_cents: v ? v.price_cents : p.base_price_cents,
      product_id: p.id,
      variation_id: v ? v.id : null,
      taxable: p.taxable !== false,
    };
    setItems(prev => {
      const cleaned = prev.filter(it => it.name || it.description || it.unit_price_cents);
      return [...cleaned, line];
    });
  };

  const doSubmit = async (asDraft) => {
    setBusy(true); setErr("");
    try {
      const res = await apiFetch(`${API_BASE}/admin/invoices/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...customer, items, kind, notes: notes || null,
          options_mode: kind === "quote" ? optionsMode : false,
          number_override: numberOverride.trim() || null,
          revision_of: initial?.revision_of || null,
          terms_text: termsText.trim() || null,
          terms_days: termsDays,
          tax_province: province,
          seat_ledger_board_id: initial?.seat_ledger_board_id || null,
          draft: asDraft,
          edit_id: initial?.edit_id || null,
        }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `Failed to save ${kind}`); }
      if (asDraft) {
        const d = await res.json();
        toast.success(`Draft ${d.invoice_number} saved — edit it anytime from the list`);
      } else {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${kind}-${numberOverride.trim() || autoNumber || "document"}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success(`${kind === "quote" ? "Quote" : "Invoice"} generated — PDF downloaded`);
      }
      await onDone();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    await doSubmit(false);
  };

  return (
    <form onSubmit={submit} className="mb-5 p-4 rounded-2xl bg-[#DCDEE2] border border-[#C4C8CE]" data-testid="document-builder">
      <div className="font-bold font-display mb-3 flex items-center gap-2">
        <Receipt size={18} weight="duotone" color={kind === "quote" ? "#00B8D4" : "#A3B800"} />
        {initial?.edit_id
          ? `Edit draft ${initial?.number || ""}`
          : initial?.revision_of
            ? `Revise ${kind} ${initial.revision_of}`
            : (kind === "quote" ? "New quote" : "New invoice")}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form */}
        <div className="flex flex-col gap-3 min-w-0">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="doc-number-override" className="input-base" disabled={!!initial?.edit_id}
              placeholder={autoNumber ? (initial?.edit_id ? autoNumber : `Auto: ${autoNumber} (override?)`) : "Number: …"}
              value={initial?.edit_id ? (initial?.number || "") : numberOverride}
              onChange={(e) => setNumberOverride(e.target.value)} />
            <input data-testid="invoice-customer-email" className="input-base" type="email" placeholder="Customer email (optional)" value={customer.customer_email} onChange={(e) => setCustomer({ ...customer, customer_email: e.target.value })} />
          </div>
          <input data-testid="invoice-customer-phone" className="input-base" type="tel" placeholder="Customer phone (optional)" value={customer.customer_phone} onChange={(e) => setCustomer({ ...customer, customer_phone: e.target.value })} />
          {(customers || []).length > 0 && (
            <select data-testid="pick-customer" className="input-base text-sm" value={customer.customer_id || ""}
              onChange={(e) => {
                const c = (customers || []).find((x) => x.id === e.target.value);
                if (c) setCustomer({ customer_id: c.id, customer_name: c.name, customer_address: c.address || "", customer_email: c.email || "", customer_phone: c.phone || "" });
                else setCustomer({ ...customer, customer_id: null });
              }}>
              <option value="">— pick a saved customer (auto-fills name, address, email) —</option>
              {(customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <input data-testid="invoice-customer-name" className="input-base" placeholder="Customer name (e.g. Maple Ridge Secondary)" required value={customer.customer_name} onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value, customer_id: null })} />          <textarea data-testid="invoice-customer-address" className="input-base" rows={2} placeholder="Customer address (use new lines)" required value={customer.customer_address} onChange={(e) => setCustomer({ ...customer, customer_address: e.target.value })} />
          {taxRates.length > 0 && (
            <label className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] text-[#000000]">
              Tax province
              <select data-testid="doc-tax-province" className="input-base flex-1 normal-case tracking-normal" value={province} onChange={(e) => setProvince(e.target.value)}>
                {taxRates.map((t) => <option key={t.code} value={t.code}>{t.label} — {t.lines.map((l) => `${l.name} ${(l.rate * 100).toLocaleString()}%`).join(" + ")}</option>)}
              </select>
            </label>
          )}
          <div className="text-xs uppercase tracking-[0.15em] text-[#000000] mt-1">Line items</div>
          {kind === "quote" && (
            <label className="flex items-start gap-2 text-sm font-semibold cursor-pointer rounded-lg border border-[#C4C8CE] bg-white/60 p-2"
              title="Each line becomes Option 1, 2, 3… with its own tax-inclusive total. No combined total is shown.">
              <input type="checkbox" className="mt-0.5" data-testid="options-mode-checkbox"
                checked={optionsMode} onChange={(e) => setOptionsMode(e.target.checked)} />
              <span>
                Options quote — each line is a separate option
                <span className="block text-xs font-normal text-[#000000]">Every line gets its own total (tax included). The combined Subtotal/Total section is skipped — the customer picks ONE option.</span>
              </span>
            </label>
          )}
          <select data-testid="add-saved-product" className="input-base text-sm" value="" onChange={(e) => e.target.value && addFromCatalog(e.target.value)}>
            <option value="">+ Add from the catalogue…</option>
            {catalogOptions.length === 0 && <option value="" disabled>Catalogue is empty — add a product in the Master catalogue card on this tab</option>}
            {catalogOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="flex flex-col gap-2">
            {items.map((it, i) => (
              <div key={i} className="rounded-lg border border-[#C4C8CE] bg-white/70 p-2 flex flex-col gap-2" data-testid={`invoice-item-${i}`}>
                <div className="flex gap-2 items-center">
                  <input data-testid={`invoice-item-${i}-name`} className="input-base flex-1 min-w-0" placeholder="Item name" required value={it.name || ""} onChange={(e) => setItem(i, "name", e.target.value)} />
                  <input data-testid={`invoice-item-${i}-qty`} className="input-base text-right" style={{ width: "6.5rem", flex: "0 0 auto" }} type="number" min="1" max="99999" placeholder="Qty" required value={it.quantity} onChange={(e) => setItem(i, "quantity", Number(e.target.value))} />
                  <input data-testid={`invoice-item-${i}-price`} className="input-base text-right" style={{ width: "8.5rem", flex: "0 0 auto" }} type="number" min="0" step="0.01" placeholder="Unit $"
                    value={it.unit_price_cents ? it.unit_price_cents / 100 : ""}
                    onChange={(e) => setItem(i, "unit_price_cents", e.target.value.trim() ? Math.round(parseFloat(e.target.value) * 100) || 0 : 0)} />
                  <button type="button" className="btn-ghost shrink-0" onClick={() => removeItem(i)} disabled={items.length === 1}>
                    <Trash size={16} />
                  </button>
                </div>
                <div className="flex gap-2 items-center">
                  <input data-testid={`invoice-item-${i}-description`} className="input-base text-sm flex-1 min-w-0" placeholder="Description (optional — shown under the item name)" value={it.description || ""} onChange={(e) => setItem(i, "description", e.target.value)} />
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-[#000000] whitespace-nowrap cursor-pointer shrink-0"
                    title="Untick for tax-exempt items — no tax added for this line and the PDF shows an exempt note">
                    <input type="checkbox" data-testid={`invoice-item-${i}-taxable`} checked={it.taxable !== false} onChange={(e) => setItem(i, "taxable", e.target.checked)} />
                    Add tax
                  </label>
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="btn-secondary self-start text-sm" onClick={addItem} data-testid="add-line-item">+ Add line</button>
          <textarea data-testid="doc-notes" className="input-base" rows={2} placeholder={kind === "quote" ? "Notes shown on the quote (validity, terms…)" : "Notes shown on the invoice (payment instructions…)"} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <label className="flex flex-col gap-1 text-xs uppercase tracking-[0.1em] text-[#000000]">
            Document footer (printed at the bottom — day counts are read from it)
            <textarea data-testid="doc-footer" className="input-base normal-case tracking-normal" rows={2}
              value={termsText} onChange={(e) => setTermsText(e.target.value)} />
            <span className="normal-case tracking-normal font-bold" style={{ color: "#3F6212" }} data-testid="doc-terms-date">
              → {kind === "quote" ? "valid until" : "due"} {termsEndDate} ({termsDays} days{parsedDays === null ? " — default; no day count found in the footer text" : ""})
            </span>
          </label>
          {err && <div className="text-[#FF5100] text-sm">{err}</div>}
          <div className="flex gap-2 flex-wrap">
            <button type="submit" className="btn-primary" disabled={busy} data-testid="invoice-submit">
              {busy ? "Working..." : `Generate PDF ${kind}`}
            </button>
            <button type="button" className="btn-secondary" disabled={busy} data-testid="save-draft-btn"
              title="Save without issuing a PDF — come back and edit it later"
              onClick={(e) => { const f = e.currentTarget.closest("form"); if (f && f.reportValidity()) doSubmit(true); }}>
              Save draft
            </button>
            <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          </div>
        </div>

        {/* Live WYSIWYG preview */}
        <DocumentPreview kind={kind} identity={identity} customer={customer} items={items}
          number={numberOverride.trim() || autoNumber} notes={notes} taxLines={taxLines} terms={defaultTerms} footerText={termsText}
          optionsMode={kind === "quote" && optionsMode}
          dueLine={`${kind === "quote" ? "Valid until" : "Due"}: ${termsEndDate}`} />
      </div>
    </form>
  );
}

function DocumentPreview({ kind, identity, customer, items, number, notes, taxLines, terms, footerText, dueLine, optionsMode }) {
  const subtotal = items.reduce((s, it) => s + (it.quantity || 0) * (it.unit_price_cents || 0), 0);
  const taxableSubtotal = items.reduce((s, it) => s + (it.taxable !== false ? (it.quantity || 0) * (it.unit_price_cents || 0) : 0), 0);
  const hasExempt = items.some((it) => it.taxable === false);
  const rateSum = (taxLines || [{ rate: 0.05 }]).reduce((s, l) => s + (l.rate || 0), 0);
  const lines = (taxLines || [{ name: "GST", rate: 0.05 }]).map((l) => ({
    label: `${l.name} (${(l.rate * 100).toLocaleString()}%)`,
    cents: Math.round(taxableSubtotal * l.rate),
  }));
  const taxNames = [...new Set((taxLines || [{ name: "GST" }]).map((l) => l.name))].join(" + ");
  const tax = lines.reduce((s, l) => s + l.cents, 0);
  const total = subtotal + tax;
  const ident = identity || {};
  return (
    <div className="rounded-xl bg-white border border-[#C4C8CE] p-6 shadow-sm min-w-0 overflow-hidden" data-testid="document-preview"
      style={{ fontSize: "11px", color: "#000000", aspectRatio: "210 / 297", overflowY: "auto" }}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          {ident.logo_url && <img src={ident.logo_url} alt="logo" className="h-9 w-auto max-w-[140px] object-contain shrink-0" />}
          <div className="font-bold text-base leading-tight break-words">{ident.business_name || "Your business"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-extrabold text-base tracking-wide">{kind === "quote" ? "QUOTE" : "INVOICE"}</div>
          <div># {number || "…"}</div>
          <div>{new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}</div>
          {dueLine && <div className="font-semibold" data-testid="preview-due-line">{dueLine}</div>}
        </div>
      </div>
      <div className="border-t border-[#0A0D14] my-3" />
      <div className="flex justify-between gap-4 mb-4">
        <div className="min-w-0 break-words" style={{ maxWidth: "45%" }}>
          {(ident.address || "").split(",").map((l, i) => <div key={i}>{l.trim()}</div>)}
          {ident.gst_number && <div>GST/HST #: {ident.gst_number}</div>}
          {(ident.email || ident.phone) && <div>{[ident.email, ident.phone].filter(Boolean).join(" · ")}</div>}
        </div>
        <div className="min-w-0 break-words" style={{ width: "50%" }}>
          <div className="font-bold">{kind === "invoice" ? "Bill to" : "Prepared for"}</div>
          <div>{customer.customer_name || <span className="text-[#000000] italic">Customer name…</span>}</div>
          {(customer.customer_address || "").split(/\n|,/).map((l) => l.trim()).filter(Boolean)
            .map((l, i) => <div key={i}>{l}</div>)}
          {customer.customer_email && <div data-testid="preview-customer-email">{customer.customer_email}</div>}
          {customer.customer_phone && <div data-testid="preview-customer-phone">{customer.customer_phone}</div>}
        </div>
      </div>
      <table className="w-full mb-3 table-fixed">
        <colgroup>
          <col />
          <col style={{ width: "56px" }} />
          <col style={{ width: "84px" }} />
          <col style={{ width: "96px" }} />
        </colgroup>
        <thead>
          <tr className="text-white" style={{ background: "#0A0D14" }}>
            <th className="text-left px-2 py-1">Description</th>
            <th className="text-right px-2 py-1">Qty</th>
            <th className="text-right px-2 py-1">Unit</th>
            <th className="text-right px-2 py-1">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const line = (it.quantity || 0) * (it.unit_price_cents || 0);
            const lineTax = it.taxable === false ? 0 : Math.round(line * rateSum);
            return (
            <React.Fragment key={i}>
            <tr className="border-b border-[#ECEDEF] align-top">
              <td className="px-2 py-1 break-words">
                {optionsMode && <div className="text-[9px] font-extrabold tracking-wider" style={{ color: "#007A8F" }}>OPTION {i + 1}</div>}
                <div>{it.name || it.description || <span className="text-[#000000] italic">…</span>}</div>
                {it.name && it.description ? <div className="text-[10px]">{it.description}</div> : null}
              </td>
              <td className="px-2 py-1 text-right">{(it.quantity || 0).toLocaleString()}</td>
              <td className="px-2 py-1 text-right">{fmt(it.unit_price_cents)}</td>
              <td className="px-2 py-1 text-right">{fmt(line)}{it.taxable === false ? " *" : ""}</td>
            </tr>
            {optionsMode && (
              <tr className="border-b border-[#ECEDEF]">
                <td colSpan={4} className="px-2 py-1 text-right font-bold" data-testid={`preview-option-total-${i}`}>
                  {lineTax
                    ? `Option ${i + 1} total: ${fmt(line)} + ${taxNames} ${fmt(lineTax)} = ${fmt(line + lineTax)} CAD`
                    : `Option ${i + 1} total: ${fmt(line)} CAD (tax exempt)`}
                </td>
              </tr>
            )}
            </React.Fragment>
          );})}
        </tbody>
      </table>
      {optionsMode ? (
        <div className="mb-3 italic text-[10px] text-[#000000]" data-testid="preview-options-note">
          This quote presents {items.length} option{items.length !== 1 ? "s" : ""} — each is priced individually with tax shown per option. Choose the one that fits.
          {hasExempt && <div>* {taxNames} exempt</div>}
        </div>
      ) : (
      <div className="flex flex-col items-end gap-0.5 mb-3 relative">
        {hasExempt && <div className="absolute left-0 top-0 italic text-[10px] text-[#000000]" data-testid="preview-exempt-note">* {taxNames} exempt</div>}
        <div className="flex gap-6"><span>Subtotal</span><span className="w-24 text-right">{fmt(subtotal)}</span></div>
        {lines.map((l) => (
          <div key={l.label} className="flex gap-6"><span>{l.label}</span><span className="w-24 text-right">{fmt(l.cents)}</span></div>
        ))}
        <div className="flex gap-6 font-extrabold text-sm"><span>Total CAD</span><span className="w-24 text-right" style={{ color: "#0A6B2C" }}>{fmt(total)}</span></div>
      </div>
      )}
      {notes && (
        <div className="mb-2">
          <div className="font-bold">Notes</div>
          <div className="whitespace-pre-line">{notes}</div>
        </div>
      )}
      <div className="text-[10px] italic border-t border-[#ECEDEF] pt-2">
        {footerText || ident.footer_note || (kind === "quote"
          ? `This quote is valid for ${terms || 30} days from the date above. Prices in CAD.`
          : "CRA-compliant invoice — GST/HST registration number included as required by Canada Revenue Agency.")}
      </div>
    </div>
  );
}
